/*
  Letters page — landing view listing saved drafts + "New letter" CTA.

  Filters by client, template and status. Each row links into the
  compose/edit screen at /letters/:id.
*/
import { fmtDateTime } from "@/lib/intl";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Mail, Plus, FileText, Send, Users2, ArrowRight, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STATUS_TONE = {
  draft:    "bg-zinc-100 text-zinc-700 border-zinc-200",
  ready:    "bg-blue-50 text-blue-800 border-blue-200",
  sent:     "bg-emerald-50 text-emerald-800 border-emerald-200",
  archived: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

export default function Letters() {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  async function refresh() {
    setLoading(true);
    try {
      const params = {};
      if (clientFilter.trim()) params.client_email = clientFilter.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      const r = await api.get("/letters", { params });
      setLetters(r.data.letters || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [clientFilter, statusFilter]);

  const stats = useMemo(() => {
    const s = { draft: 0, ready: 0, sent: 0 };
    for (const l of letters) s[l.status] = (s[l.status] || 0) + 1;
    return s;
  }, [letters]);

  return (
    <div className="mx-auto max-w-[1400px] px-6 sm:px-8 md:px-10 py-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
            <Mail className="h-3.5 w-3.5" /> Letter writer
          </div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            Clinical letters
          </h1>
          <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
            Draft GP care-plan updates and NDIS treatment reports using
            live intake, assessment and Strength by Numbers data. Kimi
            K2 (or whichever OpenRouter model you've selected) generates
            the first draft — you edit, export or email.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => navigate("/letters/gps")}
            className="border-zinc-300"
            data-testid="letters-gps-btn"
          >
            <Users2 className="h-4 w-4 mr-1.5" /> GP directory
          </Button>
          <Button
            onClick={() => navigate("/letters/new")}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="letters-new-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New letter
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Drafts" value={stats.draft || 0} />
        <StatCard label="Ready to send" value={stats.ready || 0} />
        <StatCard label="Sent" value={stats.sent || 0} />
        <StatCard label="Total on file" value={letters.length} />
      </div>

      <Card className="mt-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="p-4 border-b border-zinc-100 flex gap-3 flex-wrap items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="text-2xs uppercase tracking-wider text-zinc-500 block mb-1">
              Filter by client email
            </label>
            <Input
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              placeholder="e.g. nathan@example.com"
              className="border-zinc-300 text-sm"
              data-testid="letters-client-filter"
            />
          </div>
          <div className="min-w-[180px]">
            <label className="text-2xs uppercase tracking-wider text-zinc-500 block mb-1">
              Status
            </label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="border-zinc-300 text-sm" data-testid="letters-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : letters.length === 0 ? (
          <div className="p-10 text-center">
            <FileText className="h-8 w-8 mx-auto text-zinc-300" />
            <p className="mt-2 text-sm text-zinc-600">
              No letters yet — tap <b>New letter</b> to draft your first one.
            </p>
          </div>
        ) : (
          <Table data-testid="letters-table">
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Template</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {letters.map((l) => (
                <TableRow key={l.id} data-testid={`letter-row-${l.id}`}>
                  <TableCell>
                    <div className="font-medium text-zinc-950">
                      {l.client_name || l.client_email}
                    </div>
                    {l.client_name && (
                      <div className="text-2xs text-zinc-500">{l.client_email}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-zinc-700">
                      {l.template_id === "gp_care_plan_update" ? "GP care plan" : "NDIS treatment"}
                    </span>
                  </TableCell>
                  <TableCell>
                    {l.recipient_snapshot?.last_name ? (
                      <span className="text-xs">
                        {l.recipient_snapshot.title} {l.recipient_snapshot.first_name} {l.recipient_snapshot.last_name}
                        <span className="text-zinc-500"> · {l.recipient_snapshot.clinic}</span>
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span
                      className={cn(
                        "text-2xs px-2 py-0.5 rounded-full border capitalize",
                        STATUS_TONE[l.status] || STATUS_TONE.draft,
                      )}
                    >
                      {l.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-zinc-500 font-mono-num">
                    {l.updated_at ? fmtDateTime(l.updated_at) : ""}
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        try {
                          const r = await api.post(`/letters/${l.id}/duplicate`);
                          toast.success("Duplicated");
                          navigate(`/letters/${r.data.id}`);
                        } catch (err) {
                          toast.error("Duplicate failed", {
                            description: err?.response?.data?.detail || err.message,
                          });
                        }
                      }}
                      data-testid={`letter-dup-${l.id}`}
                      title="Duplicate as a new draft"
                      className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-950 mr-3"
                    >
                      <Copy className="h-3 w-3" /> Duplicate
                    </button>
                    <Link
                      to={`/letters/${l.id}`}
                      className="inline-flex items-center gap-1 text-xs text-zinc-700 hover:text-zinc-950"
                      data-testid={`letter-open-${l.id}`}
                    >
                      Open <ArrowRight className="h-3 w-3" />
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <Card className="p-3 rounded-md border border-zinc-200 shadow-none">
      <div className="text-2xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 text-2xl font-mono-num tabular-nums text-zinc-950">{value}</div>
    </Card>
  );
}
